/*
 * File: donaldGrumpApi.ts
 * Project: beaker
 * -----
 * Last Modified: 16 Mar 2022 10:54:03 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import lodash from 'lodash';
import { ApiMultipleResponse } from 'models/ApiModels';
import { ProductMaster, ProductMastersParams } from 'models/ProductMaster';
import { ReleasedProduct, ReleasedProductsParams } from 'models/ReleasedProduct';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_DONALD_GRUMP_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_DONALD_GRUMP;

export const donaldGrumpApi = createApi({
  reducerPath: 'donaldGrumpApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getProductMasters: builder.query<ApiMultipleResponse<ProductMaster>, ProductMastersParams>({
      query: (params) => ({
        url: '/productMasters',
        params
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().donaldGrumpApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          //
        }
      }
    }),
    getReleasedProducts: builder.query<ApiMultipleResponse<ReleasedProduct>, ReleasedProductsParams>({
      query: (params) => ({
        url: '/releasedProducts',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<ReleasedProduct> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().donaldGrumpApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          //
        }
      }
    }),
    getReleasedProduct: builder.query<ReleasedProduct, string>({
      query: (productId) => ({
        url: `/mdsi/releasedProducts/${productId}`
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetProductMastersQuery, useGetReleasedProductsQuery, useGetReleasedProductQuery, useGetVersionQuery, useGetApiNameQuery } = donaldGrumpApi;
